import { useEffect, useState } from "react";
import { getBreathConfiguration } from "../../../../common/network/Api";
import {
  PnoeConfigParameter,
  PnoeLifestyleParameter,
  PnoeParameterOption,
} from "../../entities/pnoeMeasurement";
import { sortLifestyleParameters } from "./pnoeLifestyle";

/** field_name → its option list, ready for a select. */
export type PnoeParameterOptions = Record<string, PnoeParameterOption[]>;

const toOptionMap = (rows: unknown): PnoeParameterOptions => {
  if (!Array.isArray(rows)) return {};
  const map: PnoeParameterOptions = {};
  (rows as PnoeConfigParameter[])
    .filter((row) => row?.field_name && row.is_active !== false)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .forEach((row) => {
      // Every answer, disabled ones included: a stored value may point at a
      // choice that has since been disabled, and its label must still
      // resolve on the page. The edit selects filter at the select
      // (`selectOptionsWithCurrent`).
      map[row.field_name] = Array.isArray(row.answers) ? row.answers : [];
    });
  return map;
};

/** field_name → `details`, for the questions that have any. */
const toDetailsMap = (rows: unknown): Record<string, string> => {
  if (!Array.isArray(rows)) return {};
  const map: Record<string, string> = {};
  (rows as PnoeConfigParameter[]).forEach((row) => {
    const details = typeof row?.details === "string" ? row.details.trim() : "";
    if (row?.field_name && details) map[row.field_name] = details;
  });
  return map;
};

const toLifestyleParameters = (rows: unknown): PnoeLifestyleParameter[] =>
  sortLifestyleParameters(
    Array.isArray(rows)
      ? (rows as PnoeLifestyleParameter[])
          .filter((row) => row?.field_name)
          .map((row) => ({
            ...row,
            details: typeof row.details === "string" ? row.details : "",
            answers: Array.isArray(row.answers) ? row.answers : [],
            sort_order: row.sort_order ?? 0,
          }))
      : []
  );

/**
 * The editable-question configuration of the breath service: option lists for
 * the participant and RMR-protocol fields, their help text, and the lifestyle
 * question set.
 *
 * Used to turn stored values into display labels (`3_5_per_week` → "3–5 /
 * week") and to populate the edit dropdowns. A failure is not fatal — callers
 * fall back to `titleCaseOption` on the raw value. `lifestyle` is `[]` until
 * loaded and on a configuration cached before the lifestyle set existed.
 */
export const usePnoeParameterOptions = () => {
  const [participant, setParticipant] = useState<PnoeParameterOptions>({});
  const [resting, setResting] = useState<PnoeParameterOptions>({});
  // Participant and resting field names are disjoint, so one map serves both.
  const [details, setDetails] = useState<Record<string, string>>({});
  const [lifestyle, setLifestyle] = useState<PnoeLifestyleParameter[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (loaded) return;
    let cancelled = false;
    getBreathConfiguration()
      .catch((e) => {
        console.warn("Error loading breath configuration:", e);
        return null;
      })
      .then((config) => {
        if (cancelled) return;
        setParticipant(toOptionMap(config?.participant_parameters));
        setResting(toOptionMap(config?.resting_protocol_parameters));
        setDetails({
          ...toDetailsMap(config?.participant_parameters),
          ...toDetailsMap(config?.resting_protocol_parameters),
        });
        setLifestyle(toLifestyleParameters(config?.lifestyle_parameters));
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [loaded]);

  return { participant, resting, details, lifestyle, loaded };
};
